import dayjs from 'dayjs';
import { SessionMode } from 'enums/SessionMode';
import { SessionType } from 'enums/SessionType';
import { ISessionManagerExternalProvider } from 'interfaces/providers/ISessionManagerExternalProvider';
import { ISessionManagerProvider } from 'interfaces/providers/ISessionManagerProvider';
import { IApiArchiveSessionModel } from 'interfaces/session/IApiArchiveSessionModel';
import type { ISessionMode } from 'interfaces/session/ISessionMode';
import type { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import moment from 'moment';
import UpcomingGameNotification from 'notifications/UpcomingGameNotification/UpcomingGameNotification';
import React from 'react';
import { IGameScheduleReference } from '../interfaces/session/ISessionMode';
import { IApiSessionMetadataUpdateRequest } from '../interfaces/tagging/ApiModels/IApiSessionMetadataUpdateRequest';
import { ContextualService } from '../services/data-services/ContextualService';
import { LogService } from '../services/data-services/LogService';
import { IntervalWorkerService } from '../services/helper-services/IntervalWorkerService';
import { BaseStore } from './BaseStore';
import { ContextualDataStore } from './ContextualDataStore';
import { NotificationStore } from './NotificationStore';
import { UiStore } from './UiStore';

export interface IInitialMetadata {
  gameScheduleReference?: IGameScheduleReference;
  externalSessionId: string;
  privateSession: boolean;
}
export class SessionStore extends BaseStore {
  static UPCOMING_GAME_NOTIFICATION_TIME = 3600;

  static SECOND_THRESHOLD_GAME_NOTIFICATION_TIME = 1800;

  static UPCOMING_GAME_NOTIFICATION_KEY = 'UpcomingGameCheckNotification';

  private sessionService: ISessionManagerProvider;

  private sessionServiceExternal: ISessionManagerExternalProvider;

  private contextualDataStore: ContextualDataStore;

  private intervalService: IntervalWorkerService;

  private contextualService: ContextualService;

  private notificationStore: NotificationStore;

  private uiStore: UiStore;

  constructor(
    sessionService: ISessionManagerProvider,
    sessionServiceExternal: ISessionManagerExternalProvider,
    contextualDataStore: ContextualDataStore,
    logService: LogService,
    intervalService: IntervalWorkerService,
    contextualService: ContextualService,
    notificationStore: NotificationStore,
    uiStore: UiStore
  ) {
    super(logService);
    this.sessionService = sessionService;
    this.sessionServiceExternal = sessionServiceExternal;
    this.currentSessionMode = {} as ISessionMode;
    this.currentSessionMetadata = {} as IApiSessionMetadataUpdated;
    this.archiveSessions = [] as IApiArchiveSessionModel[];
    this.archiveBattingPractices = [] as IApiArchiveSessionModel[];
    this.contextualDataStore = contextualDataStore;
    this.initialMetadata = undefined;
    this.intervalService = intervalService;
    this.contextualService = contextualService;
    this.notificationStore = notificationStore;
    this.uiStore = uiStore;
    makeObservable(this);
  }

  @observable isEndGameModalVisible = false;

  @observable currentSessionMode: ISessionMode;

  @observable currentSessionMetadata: IApiSessionMetadataUpdated;

  @observable timeout?: NodeJS.Timeout;

  @observable initialMetadata?: IInitialMetadata;

  @observable isDataBroadcasterConnected = false;

  @observable archiveSessions: IApiArchiveSessionModel[];

  @observable archiveSessionsLoad = 9;

  @observable archiveBattingPracticesLoad = 9;

  @observable archiveBattingPractices: IApiArchiveSessionModel[];

  @observable dismissedSessionsIds: string[] = [];

  @observable isOperatorlessTaggingEnabledForSelectedSession = false;

  @action setIsOperatorlessTaggingEnabledForSelectedSession(enabled: boolean) {
    this.isOperatorlessTaggingEnabledForSelectedSession = enabled;
  }

  @action private setSessionMode(mode: ISessionMode) {
    if (this.currentSessionMode && mode.sessionId !== this.currentSessionMode.sessionId) {
      this.currentSessionMetadata = {} as IApiSessionMetadataUpdated;
    }
    this.currentSessionMode = mode;
    this.logService.setSessionId(mode.sessionId || '');
  }

  @action async setCurrentSessionMode(data: ISessionMode) {
    try {
      data.time = moment().toISOString();
      // TODO: Simplify changeSessionMode response with currentSessionMode to avoid double calls
      await this.sessionServiceExternal.changeSessionMode(data, this.isOperatorlessTaggingEnabledForSelectedSession);
      const newMode = await this.sessionServiceExternal.getCurrentSessionMode();
      this.setSessionMode(newMode);
      return true;
    } catch (error) {
      return false;
    }
  }

  @action submitCalibration = async (sessionMetadata: IApiSessionMetadataUpdated) => {
    try {
      this.isLoading = true;

      if (this.currentSessionMode && this.currentSessionMode?.sessionId === sessionMetadata.sessionId) {
        sessionMetadata.time = moment().toISOString();

        if (sessionMetadata.league) {
          sessionMetadata.league.shortName = this.contextualDataStore.getLeagueShortNameById(sessionMetadata.league.id);
        }

        await this.sessionService.submitSession(sessionMetadata as IApiSessionMetadataUpdateRequest);
        this.currentSessionMetadata = sessionMetadata;
      } else {
        this.logService.logError(
          {
            message: 'Session Metadata will not be updated.',
            sessionMetadata,
            currentSessionMode: this.currentSessionMode,
          },
          { function: 'SessionStore@submitCalibration(1)' }
        );
        throw Error('Problem with updating Session Metadata. SessionId mismatch.');
      }

      await this.setCurrentSessionMode({
        ...this.currentSessionMode,
        sessionMode: SessionMode.SessionEnded,
      });
    } catch (e) {
      this.logService.logError(e, { function: 'SessionStore@submitCalibration(2)' });
      throw e;
    } finally {
      this.isLoading = false;
    }
  };

  @action submitGameFromArchives = async (sessionId: string) => {
    try {
      this.isLoading = true;
      await this.sessionService.submitSessionMetadata(sessionId);
      await this.setArchiveSessions(0, this.archiveSessionsLoad);
      await this.setArchiveBattingPractices(0, this.archiveBattingPracticesLoad);
      this.notificationStore.addNotification({
        id: 'session-submitted',
        message: 'Session submitted',
        variant: 'warning',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('session-submitted');
        },
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-submitting-review-game',
          message: error.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-submitting-review-game');
          },
        });
      }
    } finally {
      this.isLoading = false;
    }
  };

  @action setInitialMetadata(initialMetadata?: IInitialMetadata) {
    this.initialMetadata = initialMetadata;
  }

  @action setArchiveSessions = async (skip: number, take: number) => {
    this.archiveSessions = await this.sessionServiceExternal.getArchiveRegularSessions(skip, take);
  };

  @action setArchiveBattingPractices = async (skip: number, take: number) => {
    this.archiveBattingPractices = await this.sessionServiceExternal.getArchiveBpSessions(skip, take);
  };

  @action onSessionModeChanged(sessionModeChanged: ISessionMode) {
    this.setSessionMode(sessionModeChanged);

    if (sessionModeChanged.sessionMode === SessionMode.SessionEnded) {
      if (sessionModeChanged.sessionModeChangeIssuer === 'DualSystemArmingService') {
        this.uiStore.setPrimarySystemEndedModalVisible(true);
        return;
      }

      if (sessionModeChanged.sessionModeChangeIssuer && sessionModeChanged.sessionModeChangeIssuer !== 'TaggingApp') {
        this.uiStore.setSessionEndedModalVisible(true);
        return;
      }
    }
  }

  @action onSessionMetadataChanged(metadata: IApiSessionMetadataUpdated) {
    if (
      metadata.sessionId !== this.currentSessionMode.sessionId &&
      moment(this.currentSessionMetadata.sessionModeState.sessionStartedUtc).isAfter(metadata.sessionModeState.sessionStartedUtc)
    ) {
      return; // old session metadata updated. ignore
    }
    this.currentSessionMetadata = metadata;
  }

  @action setIsDataBroadcasterConnected = (isConnected: boolean) => {
    this.isDataBroadcasterConnected = isConnected;
  };

  isNewSession(newSession: ISessionMode, currentSession: ISessionMode) {
    return (
      !currentSession.sessionMode ||
      ((currentSession.sessionMode === SessionMode.Unknown || currentSession.sessionMode === SessionMode.SessionEnded) &&
        newSession.sessionMode === SessionMode.SessionStarted)
    );
  }

  @action fetchCurrentSessionMode = async () => {
    try {
      const mode = await this.sessionServiceExternal.getCurrentSessionMode();
      if (mode.sessionId === '00000000-0000-0000-0000-000000000000') {
        mode.sessionId = undefined;
      }
      this.setSessionMode(mode);
      return mode;
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-fetching-current-session-mode',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-fetching-current-session-mode');
          },
        });
      }
    }
  };

  @action fetchSessionMetadata = async (sessionId: string) => {
    try {
      const metadata = await this.sessionServiceExternal.getSessionMetadata(sessionId);
      runInAction(() => {
        this.currentSessionMetadata = metadata;
      });
      return metadata;
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-fetching-session-metadata',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-fetching-session-metadata');
          },
        });
      }
    }
  };

  @action loadSessionData = async () => {
    const mode = await this.fetchCurrentSessionMode();
    if (mode && mode.sessionId) {
      await this.fetchSessionMetadata(mode.sessionId);
    }
    return true;
  };

  @action updateSessionMetadata = async (newMetadata: IApiSessionMetadataUpdated, isReview: boolean) => {
    try {
      const mode = await this.fetchCurrentSessionMode();

      if (!isReview && (!mode || mode.sessionId !== newMetadata.sessionId)) {
        this.logService.logError(
          { message: 'Session Metadata will not be updated.', newMetadata, mode },
          { function: 'SessionStore@updateSessionMetadata' }
        );
        throw Error('Problem with updating Session Metadata. SessionId mismatch.');
      }

      newMetadata.time = moment().toISOString();
      if (newMetadata.league) {
        newMetadata.league.shortName = this.contextualDataStore.getLeagueShortNameById(newMetadata.league.id);
      }
      await this.sessionService.updateSessionMetadata(newMetadata as IApiSessionMetadataUpdateRequest);

      if (!isReview) {
        this.currentSessionMetadata = newMetadata;
      }
    } catch (e) {
      this.logService.logError(e, { function: 'SessionStore@updateSessionMetadata' });
      throw e;
    }
  };

  @action setIsEndGameModalVisible(isVisible: boolean) {
    this.isEndGameModalVisible = isVisible;
  }

  @computed get isPlaying() {
    return this.currentSessionMode.sessionMode === SessionMode.PlayingStarted;
  }

  @computed get isWarmup() {
    return this.currentSessionMode.sessionMode === SessionMode.WarmupStarted;
  }

  @computed get isSessionStarted() {
    return this.currentSessionMode.sessionMode === SessionMode.SessionStarted;
  }

  @computed get isRadarReady() {
    return (
      this.currentSessionMode.sessionMode === SessionMode.WarmupStarted ||
      this.currentSessionMode.sessionMode === SessionMode.SessionStarted ||
      this.currentSessionMode.sessionMode === SessionMode.SessionEnded
    );
  }

  @computed get isMetadataLineupSet() {
    return (
      this.currentSessionMetadata.sessionTaggingState &&
      this.currentSessionMetadata.sessionTaggingState.initialLineupSetUtc &&
      (this.currentSessionMode.sessionType === SessionType.BattingPractice || this.currentSessionMetadata.awayTeam) &&
      this.currentSessionMetadata.homeTeam
    );
  }

  @computed get isGameInProgress() {
    return (
      this.currentSessionMode.sessionMode === SessionMode.WarmupStarted ||
      this.currentSessionMode.sessionMode === SessionMode.PlayingStarted ||
      this.currentSessionMode.sessionMode === SessionMode.SessionStarted
    );
  }

  @computed get isScheduledGame() {
    return !!this.currentSessionMode.gameScheduleReference;
  }

  setupUpcomingGameCheck = () => {
    this.intervalService.addJob(
      SessionStore.UPCOMING_GAME_NOTIFICATION_KEY,
      () => {
        this.onUpcomingGameCheck();
      },
      3
    );
  };

  dismissUpcomingSessionDialog = (id: string) => {
    this.dismissedSessionsIds.push(id);
  };

  temporaryDismissedNotificationIds: string[] = [];

  temporaryDismissNotification = (id: string) => {
    this.temporaryDismissedNotificationIds.push(id);
  };

  removeTemporaryDismiss = (id: string) => {
    this.temporaryDismissedNotificationIds = this.temporaryDismissedNotificationIds.filter((sessionId) => sessionId !== id);
  };

  /**
   * Checks for upcoming games and triggers notifications if a game is starting soon (within 60 and 30 minutes).
   * Includes Ad-hoc and scheduled games.
   */
  onUpcomingGameCheck = () => {
    const nextGame = this.contextualDataStore.getNextGame();

    this.checkTemporaryDismissedGames();

    if (
      !this.isGameInProgress ||
      !nextGame ||
      nextGame.stateIssued === 'WillNotBeIssued' ||
      nextGame.externalSessionId === this.currentSessionMode.externalSessionId ||
      nextGame.id === this.currentSessionMetadata.gameScheduleReference?.id ||
      this.temporaryDismissedNotificationIds.includes(nextGame.id) ||
      this.dismissedSessionsIds.includes(nextGame.id)
    ) {
      return;
    }

    const startingInSeconds = dayjs.utc(nextGame.armingTime).diff(dayjs.utc(), 'seconds');

    if (startingInSeconds > 0 && startingInSeconds < SessionStore.UPCOMING_GAME_NOTIFICATION_TIME) {
      this.notificationStore.addNotification({
        id: `upcoming-game`,
        message: (
          <UpcomingGameNotification
            onDissmissCallback={() => this.notificationStore.removeNotification(`upcoming-game`)}
            onConfirmCallback={async () => {
              await this.contextualService.DisableScheduleAutoStart(nextGame.id);
              this.dismissUpcomingSessionDialog(nextGame.id);
              this.notificationStore.removeNotification(`upcoming-game`);
              this.notificationStore.addToast({
                header: 'Next game delayed',
                message: `You can set the next game up from the schedule when you're ready.`,
                severity: 'success',
                open: true,
                onClose: () => {
                  this.notificationStore.removeToast();
                },
              });
            }}
            externalSessionId={nextGame.externalSessionId}
            armingTime={nextGame.armingTime}
            timeRange={SessionStore.UPCOMING_GAME_NOTIFICATION_TIME}
            sessionId={nextGame.id}
          />
        ),
        variant: 'error',
        removable: false,
      });
    }
  };

  /**
   * Checks if temporary dismissed upcoming sessions are closer than the threshold time.
   */
  private checkTemporaryDismissedGames = () => {
    this.temporaryDismissedNotificationIds.forEach((sessionId) => {
      const upcomingSession = this.contextualDataStore.scheduledGames.find((game) => game.id === sessionId);

      if (
        upcomingSession &&
        dayjs.utc(upcomingSession.armingTime).diff(dayjs.utc(), 'seconds') < SessionStore.SECOND_THRESHOLD_GAME_NOTIFICATION_TIME
      ) {
        this.removeTemporaryDismiss(upcomingSession.id);
      }
    });
  };
}
